// noteFormat.js — tiny, dependency-free helpers for the note editor:
//   • markdownToHtml — convert LEGACY stored notes (**bold**, *italic*, "- …")
//     into the HTML shape the new WYSIWYG editor uses, so pre-existing notes
//     keep their formatting instead of showing raw asterisks.
//   • sanitizeHtml  — strict allow-list: keep ONLY b/strong/i/em/u/br/p/div/
//     ul/ol/li, drop every attribute, drop everything else. Notes are
//     private but we still sanitize so a user can't self-XSS by pasting rich
//     content with onclick/javascript: URLs.
//   • looksLikeHtml — cheap check so we only convert markdown when the stored
//     value isn't already HTML.
//
// NO regex-based HTML sanitation — we parse via DOMParser and walk the tree
// so attribute injection / nested tag tricks can't slip through.

const ALLOWED_TAGS = new Set(["B", "STRONG", "I", "EM", "U", "BR", "P", "DIV", "UL", "OL", "LI"]);

export function looksLikeHtml(s) {
  if (!s || typeof s !== "string") return false;
  // A single char like "<" or "<3" doesn't count — require an actual tag.
  return /<\/?(b|strong|i|em|u|br|p|div|ul|ol|li)[\s>/]/i.test(s);
}

export function sanitizeHtml(input) {
  if (!input) return "";
  const doc = new DOMParser().parseFromString(`<div>${input}</div>`, "text/html");
  const root = doc.body.firstChild;
  if (!root) return "";
  walk(root);
  return root.innerHTML;
}

function walk(node) {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === 3) continue;           // text node — leave alone
    if (child.nodeType !== 1) { child.remove(); continue; }  // comments etc.
    if (!ALLOWED_TAGS.has(child.tagName)) {
      // Unwrap unknown tags — keep their text so paste-from-anywhere doesn't
      // nuke the user's content, just strip the hostile wrapper.
      const text = child.textContent;
      child.replaceWith(doc().createTextNode(text));
      continue;
    }
    // Strip ALL attributes — none are allowed. (Style/class/onclick etc.)
    for (const attr of Array.from(child.attributes)) child.removeAttribute(attr.name);
    walk(child);
  }
}
function doc() { return window.document; }

/** Convert the legacy markdown-flavoured note into the HTML the new editor
 *  understands. Mirrors the toolbar's outputs exactly:
 *    **x** → <strong>x</strong>
 *    *x*   → <em>x</em>
 *    lines starting with "- " → <ul><li>…</li></ul> (consecutive lines group)
 *  Everything else is wrapped in <div> per line (contentEditable's natural
 *  block for a line of plain text). */
export function markdownToHtml(src) {
  if (!src) return "";
  const lines = src.split("\n");
  const out = [];
  let inList = false;
  for (const line of lines) {
    if (/^-\s+/.test(line)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${inline(line.replace(/^-\s+/, ""))}</li>`);
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      const t = inline(line);
      out.push(t ? `<div>${t}</div>` : "<div><br></div>");
    }
  }
  if (inList) out.push("</ul>");
  return out.join("");
}

/** Escape the raw text first, THEN substitute markdown patterns. Order
 *  matters: bold before italic so `**x**` doesn't match `*x*` twice. */
function inline(raw) {
  let s = esc(raw);
  s = s.replace(/\*\*([^\n*][^\n]*?)\*\*/g, (_, g) => `<strong>${g}</strong>`);
  s = s.replace(/(^|[^*])\*([^*\n][^\n]*?)\*(?!\*)/g, (_, pre, g) => `${pre}<em>${g}</em>`);
  return s;
}
function esc(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
